import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Fan, Thermometer, Wrench, Flame, Shield, Zap, Star, ArrowRight, Phone, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_BG = "https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/6ez9u617_IMG_6765.jpeg";
const LOGO_ORB = "https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/03qpof76_613F63E1-88E2-4F0B-BD57-37714891EDA5.png";

const iconMap = {
  fan: Fan,
  thermometer: Thermometer,
  wrench: Wrench,
  flame: Flame,
  shield: Shield,
  zap: Zap,
};

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/reviews`)
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setReviews(reviewsRes.data.slice(0, 3));
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#050505]">
      {/* Hero Section - Dark neon with photo background */}
      <section
        className="hero-section relative min-h-screen flex items-center"
        data-testid="hero-section"
      >
        <div className="hero-bg-image" style={{ backgroundImage: `url(${HERO_BG})` }}></div>
        <div className="hero-overlay"></div>

        <div className="container-custom relative z-10 pt-40 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 glass-panel px-4 py-2 mb-6 animate-fade-in-up">
                <Shield className="w-5 h-5 text-[#5b93ff]" />
                <span className="text-sm font-medium tracking-wide">Licensed &amp; Insured • CLSB# 1054485</span>
              </div>

              <h1 className="font-heading font-black text-responsive-hero mb-6 animate-fade-in-up animation-delay-100">
                YOUR COMFORT IS<br />
                <span className="neon-blue-text">OUR PRIORITY</span>
              </h1>

              <p className="text-gray-300 text-xl mb-8 max-w-xl animate-fade-in-up animation-delay-200">
                Expert heating and air conditioning for the Greater Sacramento Area.
                Fast, reliable, and affordable — available 24/7 for emergencies.
              </p>

              <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
                <Link to="/booking">
                  <Button
                    className="btn-primary text-lg px-8 py-6"
                    data-testid="hero-book-btn"
                  >
                    Book Service Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:7073843409">
                  <Button
                    className="btn-urgent text-lg px-8 py-6"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    707-384-3409
                  </Button>
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6 animate-fade-in-up animation-delay-400">
                {[
                  { icon: CheckCircle, text: "Same Day Service" },
                  { icon: Shield, text: "Licensed Technicians" },
                  { icon: Zap, text: "24/7 Emergency" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-300">
                    <item.icon className="w-5 h-5 text-[#5b93ff]" />
                    <span className="text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Logo Orb */}
            <div className="hidden lg:flex justify-center animate-float">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[#1E6BFF]/30 blur-3xl scale-110"></div>
                <img
                  src={LOGO_ORB}
                  alt="Virgil's Heating and Air"
                  className="relative h-80 w-auto rounded-full ring-1 ring-white/10 img-sharp"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-[#050505]" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">What We Do</p>
            <h2 className="font-heading font-bold text-responsive-section gradient-text mb-4">
              OUR SERVICES
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From installations to repairs, we&apos;ve got all your HVAC needs covered
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Fan;
              return (
                <div
                  key={service.id}
                  className="service-card group"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="icon-container w-16 h-16 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-8 h-8 text-[#5b93ff]" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 2).map((feature, i) => (
                      <li key={i} className="text-gray-400 text-xs flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#5b93ff]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button className="btn-primary" data-testid="view-all-services-btn">
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#0a0a0a] border-y border-white/5" data-testid="why-choose-us-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Why Virgil&apos;s</p>
            <h2 className="font-heading font-bold text-responsive-section gradient-text">
              THE VIRGIL&apos;S DIFFERENCE
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Licensed & Insured", desc: "CLSB# 1054485 - Fully licensed for your protection", color: "blue" },
              { icon: Zap, title: "Fast Response", desc: "24/7 emergency service with rapid response", color: "red" },
              { icon: Star, title: "5-Star Rated", desc: "Top-rated by our satisfied customers", color: "blue" },
              { icon: Wrench, title: "Expert Technicians", desc: "Factory-trained with years of experience", color: "red" },
            ].map((item, index) => (
              <div
                key={index}
                className="glass-panel p-8 text-center transition-all duration-300 hover:-translate-y-1 card-glow"
                data-testid={`why-choose-${index}`}
              >
                <div className={`${item.color === 'red' ? 'icon-container-red' : 'icon-container'} w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl`}>
                  <item.icon className={`w-10 h-10 ${item.color === 'red' ? 'text-[#ff6a5c]' : 'text-[#5b93ff]'}`} />
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Showcase */}
      <section className="section-padding bg-[#050505]" data-testid="brand-showcase-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative group" data-testid="brand-showcase-image">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-[#1E6BFF]/30 to-[#FF2D1A]/30 blur-2xl opacity-70 group-hover:opacity-100 transition-opacity"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/jscgxwuf_IMG_6765.jpeg"
                alt="Virgil's Heating and Air neon sign on display"
                className="relative w-full h-auto rounded-3xl border border-white/10 shadow-2xl img-sharp"
              />
            </div>
            <div>
              <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Our Promise</p>
              <h2 className="font-heading font-black text-responsive-section mb-6 text-white uppercase">
                Comfort You Can <span className="neon-blue-text">See &amp; Feel</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6 max-w-xl">
                From the first call to the final inspection, Virgil&apos;s brings craftsmanship,
                honesty, and a warm glow to every home in the Greater Sacramento Area.
                Whether it&apos;s a sweltering summer or a cold winter night, we keep your
                space exactly how you like it.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Upfront, honest pricing — no surprises",
                  "Clean, respectful, on-time technicians",
                  "Workmanship you can count on for years",
                ].map((point, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-[#5b93ff] shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <Link to="/booking">
                  <Button className="btn-primary" data-testid="showcase-book-btn">
                    Schedule Service
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:7073843409">
                  <Button className="btn-urgent" data-testid="showcase-call-btn">
                    <Phone className="mr-2 w-4 h-4" />
                    707-384-3409
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Manufacturers */}
      <section className="py-16 bg-[#050505] border-b border-white/5" data-testid="manufacturers-section">
        <div className="container-custom">
          <div className="text-center mb-10">
            <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Trusted Brands</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white">
              FEATURED MANUFACTURERS
            </h2>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
            <div className="flex items-center justify-center opacity-70 hover:opacity-100 transition-all duration-300" data-testid="manufacturer-ge">
              <svg className="h-14 w-14" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="48" fill="none" stroke="#5b93ff" strokeWidth="4"/>
                <text x="50" y="62" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#5b93ff">GE</text>
              </svg>
            </div>

            <div className="flex items-center justify-center opacity-70 hover:opacity-100 transition-all duration-300" data-testid="manufacturer-mitsubishi">
              <div className="text-center">
                <svg className="h-10 w-10 mx-auto mb-1" viewBox="0 0 100 100">
                  <polygon points="50,5 95,80 5,80" fill="#ff6a5c"/>
                  <polygon points="50,35 72,75 28,75" fill="#050505"/>
                  <polygon points="50,50 61,70 39,70" fill="#ff6a5c"/>
                </svg>
                <span className="text-xs font-bold text-white tracking-wide">MITSUBISHI</span>
              </div>
            </div>

            <div className="flex items-center justify-center opacity-70 hover:opacity-100 transition-all duration-300" data-testid="manufacturer-york">
              <span className="text-3xl font-black text-[#5b93ff] tracking-tight">YORK</span>
            </div>

            <div className="flex items-center justify-center opacity-70 hover:opacity-100 transition-all duration-300" data-testid="manufacturer-bosch">
              <div className="flex items-center gap-2">
                <svg className="h-8 w-8" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#ff6a5c" strokeWidth="8"/>
                  <circle cx="50" cy="50" r="20" fill="#ff6a5c"/>
                </svg>
                <span className="text-2xl font-bold text-white">BOSCH</span>
              </div>
            </div>

            <div className="flex items-center justify-center opacity-70 hover:opacity-100 transition-all duration-300" data-testid="manufacturer-trane">
              <span className="text-3xl font-black text-[#5b93ff] tracking-tight">TRANE</span>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Preview */}
      {reviews.length > 0 && (
        <section className="section-padding bg-[#0a0a0a] border-b border-white/5" data-testid="reviews-preview-section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Testimonials</p>
              <h2 className="font-heading font-bold text-responsive-section gradient-text">
                WHAT OUR CUSTOMERS SAY
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className="glass-panel p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#1E6BFF]/50"
                  data-testid={`review-preview-${index}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < review.rating ? 'text-[#5b93ff] fill-[#5b93ff]' : 'text-gray-700'}`}
                      />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic">&quot;{review.review_text}&quot;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#1E6BFF] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-[0_0_18px_rgba(30,107,255,0.5)]">
                      {review.customer_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white">{review.customer_name}</p>
                      <p className="text-[#5b93ff] text-sm">{review.service_type}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/reviews">
                <Button variant="outline" className="btn-outline" data-testid="view-all-reviews-btn">
                  Read All Reviews
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-[#050505] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#1E6BFF]/20 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#FF2D1A]/20 rounded-full blur-[120px]"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="font-heading font-black text-4xl md:text-5xl mb-6 uppercase">
              Ready To Get <span className="neon-blue-text">Comfortable?</span>
            </h2>
            <p className="text-gray-300 text-xl mb-8">
              Schedule your service today or call us for emergency repairs.
              Your comfort is just one call away.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/booking">
                <Button className="btn-primary text-lg px-10 py-6" data-testid="cta-book-btn">
                  Schedule Service
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:7073843409">
                <Button className="btn-urgent text-lg px-10 py-6" data-testid="cta-call-btn">
                  <Phone className="mr-2 w-5 h-5" />
                  707-384-3409
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
